import React, { useState, useEffect, useCallback } from 'react';
import { Target, Plus, Trash2, Loader2, TrendingUp, TrendingDown, Minus, Trophy, BarChart3, X } from 'lucide-react';
import { salesTargetAPI, authAPI } from '../../services/api';
import { toast } from 'sonner';

const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const quarters = ['Q1 (Jan-Mar)', 'Q2 (Apr-Jun)', 'Q3 (Jul-Sep)', 'Q4 (Oct-Dec)'];

const SalesTargets = () => {
  const [achievement, setAchievement] = useState(null);
  const [targets, setTargets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [salesmen, setSalesmen] = useState([]);
  const [year, setYear] = useState(new Date().getFullYear());
  const [periodType, setPeriodType] = useState('monthly');
  const [showSetTarget, setShowSetTarget] = useState(false);
  const [targetForm, setTargetForm] = useState({ salesman_id: '', period: 1, target_amount: '' });
  const [submitting, setSubmitting] = useState(false);
  const user = JSON.parse(localStorage.getItem('prismoline_user') || '{}');
  const canEdit = ['Admin', 'Manager'].includes(user.role);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [ach, tgt, sm] = await Promise.all([
        salesTargetAPI.getAchievement({ year, period_type: periodType }),
        salesTargetAPI.getAll({ year, period_type: periodType }),
        authAPI.listUsers().catch(() => []),
      ]);
      setAchievement(ach);
      setTargets(tgt);
      const allSalesmen = (sm || []).filter(u => ['Salesman', 'Manager', 'Admin'].includes(u.role));
      setSalesmen(allSalesmen);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [year, periodType]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSetTarget = async () => {
    if (!targetForm.salesman_id || !targetForm.target_amount) { toast.error('Fill all fields'); return; }
    try {
      setSubmitting(true);
      await salesTargetAPI.create({
        salesman_id: targetForm.salesman_id,
        year,
        period_type: periodType,
        period: Number(targetForm.period),
        target_amount: Number(targetForm.target_amount),
      });
      toast.success('Target saved');
      setShowSetTarget(false);
      setTargetForm({ salesman_id: '', period: 1, target_amount: '' });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSubmitting(false); }
  };

  const handleDeleteTarget = async (id) => {
    if (!window.confirm('Delete this target?')) return;
    try {
      await salesTargetAPI.delete(id);
      toast.success('Target deleted');
      fetchData();
    } catch (err) { toast.error('Failed to delete'); }
  };

  const fmtAmount = (v) => {
    if (!v) return '0';
    return v >= 100000 ? `${(v / 100000).toFixed(1)}L` : v.toLocaleString();
  };

  const getPctColor = (pct) => {
    if (pct >= 100) return '#059669';
    if (pct >= 75) return '#2563eb';
    if (pct >= 50) return '#d97706';
    if (pct >= 25) return '#ea580c';
    return '#dc2626';
  };

  const getPctBg = (pct) => {
    if (pct >= 100) return 'bg-green-50 text-green-700';
    if (pct >= 75) return 'bg-blue-50 text-blue-700';
    if (pct >= 50) return 'bg-amber-50 text-amber-700';
    return 'bg-red-50 text-red-700';
  };

  const currentPeriod = achievement?.current_period || (periodType === 'monthly' ? new Date().getMonth() + 1 : Math.ceil((new Date().getMonth() + 1) / 3));

  // Summary stats
  const totalTarget = achievement?.achievements?.reduce((s, a) => s + a.total_target, 0) || 0;
  const totalAchieved = achievement?.achievements?.reduce((s, a) => s + a.total_achieved, 0) || 0;
  const overallPct = totalTarget > 0 ? Math.round(totalAchieved / totalTarget * 100) : 0;
  const topPerformer = achievement?.achievements?.find(a => a.total_target > 0);

  if (loading) {
    return <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /><span className="ml-2 text-sm text-gray-500">Loading targets...</span></div>;
  }

  return (
    <div data-testid="sales-targets-module" className="space-y-5">
      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1.5 bg-gray-100 p-1 rounded-xl">
          {['monthly', 'quarterly'].map(pt => (
            <button key={pt} onClick={() => setPeriodType(pt)} data-testid={`period-${pt}`}
              className={`px-4 py-1.5 text-[11px] font-semibold rounded-lg transition-all ${periodType === pt ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}>
              {pt === 'monthly' ? 'Monthly' : 'Quarterly'}
            </button>
          ))}
        </div>
        <select value={year} onChange={e => setYear(Number(e.target.value))} data-testid="year-select"
          className="px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200">
          {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        {canEdit && (
          <button onClick={() => setShowSetTarget(true)} data-testid="set-target-btn"
            className="ml-auto flex items-center gap-2 px-4 py-2 text-xs font-semibold text-white rounded-lg hover:shadow-lg hover:shadow-blue-200"
            style={{ backgroundColor: '#24549c' }}>
            <Target className="w-3.5 h-3.5" /> Set Target
          </button>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <div className="flex items-center gap-2 mb-1">
            <Target className="w-4 h-4 text-blue-600" />
            <span className="text-[10px] text-gray-500">Total Target</span>
          </div>
          <p className="text-lg font-bold text-gray-900" style={{ fontFamily: 'DM Sans' }}>{fmtAmount(totalTarget)}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp className="w-4 h-4 text-green-600" />
            <span className="text-[10px] text-gray-500">Total Achieved</span>
          </div>
          <p className="text-lg font-bold text-gray-900" style={{ fontFamily: 'DM Sans' }}>{fmtAmount(totalAchieved)}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <div className="flex items-center gap-2 mb-1">
            <BarChart3 className="w-4 h-4" style={{ color: getPctColor(overallPct) }} />
            <span className="text-[10px] text-gray-500">Overall Achievement</span>
          </div>
          <p className="text-lg font-bold" style={{ color: getPctColor(overallPct), fontFamily: 'DM Sans' }}>{overallPct}%</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <div className="flex items-center gap-2 mb-1">
            <Trophy className="w-4 h-4 text-amber-500" />
            <span className="text-[10px] text-gray-500">Top Performer</span>
          </div>
          <p className="text-sm font-bold text-gray-900" style={{ fontFamily: 'DM Sans' }}>{topPerformer?.salesman_name || '-'}</p>
          {topPerformer && <p className="text-[10px] text-green-600 font-semibold">{topPerformer.overall_percentage}%</p>}
        </div>
      </div>

      {/* Achievement Table */}
      {(!achievement?.achievements || achievement.achievements.length === 0) ? (
        <div className="text-center py-16 bg-white rounded-xl border border-gray-100">
          <Target className="w-12 h-12 text-gray-200 mx-auto mb-3" />
          <p className="text-sm text-gray-500 font-medium">No targets set for {year}</p>
          <p className="text-xs text-gray-400 mt-1">Click "Set Target" to assign targets to salesmen</p>
        </div>
      ) : (
        <div className="space-y-4">
          {achievement.achievements.map(ach => (
            <div key={ach.salesman_id} className="bg-white rounded-xl border border-gray-100 overflow-hidden" data-testid={`achievement-${ach.salesman_id}`}>
              {/* Salesman Header */}
              <div className="flex items-center justify-between px-5 py-3 border-b border-gray-50">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold" style={{ backgroundColor: '#24549c' }}>
                    {ach.salesman_name?.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()}
                  </div>
                  <div>
                    <p className="text-sm font-bold text-gray-900">{ach.salesman_name}</p>
                    <p className="text-[10px] text-gray-400">Target: {fmtAmount(ach.total_target)} | Achieved: {fmtAmount(ach.total_achieved)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`px-3 py-1 text-xs font-bold rounded-full ${getPctBg(ach.overall_percentage)}`}>
                    {ach.overall_percentage}%
                  </span>
                  {ach.overall_percentage >= 100 && <Trophy className="w-4 h-4 text-amber-400" />}
                </div>
              </div>

              {/* Overall Progress Bar */}
              <div className="px-5 py-2 bg-gray-50/50">
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all duration-500" style={{
                    width: `${Math.min(ach.overall_percentage, 100)}%`,
                    backgroundColor: getPctColor(ach.overall_percentage),
                  }} />
                </div>
              </div>

              {/* Period Breakdown */}
              <div className="px-5 py-3">
                <div className={`grid gap-2 ${periodType === 'monthly' ? 'grid-cols-6 lg:grid-cols-12' : 'grid-cols-4'}`}>
                  {ach.periods.map(p => {
                    const label = periodType === 'monthly' ? months[p.period - 1] : quarters[p.period - 1]?.split(' ')[0];
                    return (
                      <div key={p.period}
                        className={`relative text-center p-2 rounded-lg border transition-all ${
                          p.is_current ? 'border-blue-300 bg-blue-50/50 ring-1 ring-blue-200' :
                          p.is_future ? 'border-gray-100 bg-gray-50/30 opacity-50' :
                          p.target > 0 ? 'border-gray-100 bg-white' : 'border-gray-50 bg-gray-50/30'
                        }`}
                        data-testid={`period-cell-${ach.salesman_id}-${p.period}`}
                      >
                        <p className={`text-[9px] font-bold mb-1 ${p.is_current ? 'text-blue-600' : 'text-gray-400'}`}>
                          {label} {p.is_current && '*'}
                        </p>
                        {p.target > 0 ? (
                          <>
                            <div className="h-1.5 bg-gray-100 rounded-full mb-1 overflow-hidden">
                              <div className="h-full rounded-full" style={{
                                width: `${Math.min(p.percentage, 100)}%`,
                                backgroundColor: getPctColor(p.percentage),
                              }} />
                            </div>
                            <p className="text-[10px] font-bold" style={{ color: getPctColor(p.percentage) }}>{p.percentage}%</p>
                            <p className="text-[8px] text-gray-400">{fmtAmount(p.achieved)}/{fmtAmount(p.target)}</p>
                            {p.oc_count > 0 && <p className="text-[8px] text-gray-300">{p.oc_count} OCs</p>}
                          </>
                        ) : (
                          <p className="text-[9px] text-gray-300 mt-1">No target</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Active Targets List */}
      {targets.length > 0 && canEdit && (
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <h3 className="text-xs font-bold text-gray-900 mb-3 flex items-center gap-1.5">
            <Target className="w-3.5 h-3.5" style={{ color: '#24549c' }} /> Active Targets ({targets.length})
          </h3>
          <div className="space-y-1.5">
            {targets.map(t => (
              <div key={t.id} className="flex items-center justify-between px-3 py-2 rounded-lg bg-gray-50 hover:bg-gray-100 group" data-testid={`target-row-${t.id}`}>
                <div className="flex items-center gap-3">
                  <span className="text-xs font-semibold text-gray-900">{t.salesman_name}</span>
                  <span className="text-[10px] text-gray-400">
                    {periodType === 'monthly' ? months[t.period - 1] : quarters[t.period - 1]?.split(' ')[0]} {t.year}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold" style={{ color: '#24549c' }}>{fmtAmount(t.target_amount)}</span>
                  <button onClick={() => handleDeleteTarget(t.id)} className="opacity-0 group-hover:opacity-100 p-1 text-gray-300 hover:text-red-500" data-testid={`delete-target-${t.id}`}>
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Set Target Dialog */}
      {showSetTarget && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md" data-testid="set-target-dialog">
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <h2 className="text-base font-bold" style={{ fontFamily: 'DM Sans' }}>Set Sales Target</h2>
              <button onClick={() => setShowSetTarget(false)} className="p-1 rounded-lg hover:bg-gray-100"><X className="w-4 h-4 text-gray-400" /></button>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="block text-[11px] font-medium text-gray-700 mb-1">Salesman *</label>
                <select value={targetForm.salesman_id} onChange={e => setTargetForm({ ...targetForm, salesman_id: e.target.value })} data-testid="target-salesman-select"
                  className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200">
                  <option value="">Select salesman</option>
                  {salesmen.map(s => <option key={s.id} value={s.id}>{s.name} ({s.role})</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-medium text-gray-700 mb-1">
                    {periodType === 'monthly' ? 'Month' : 'Quarter'}
                  </label>
                  <select value={targetForm.period} onChange={e => setTargetForm({ ...targetForm, period: Number(e.target.value) })} data-testid="target-period-select"
                    className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200">
                    {periodType === 'monthly'
                      ? months.map((m, i) => <option key={i} value={i + 1}>{m}</option>)
                      : quarters.map((q, i) => <option key={i} value={i + 1}>{q}</option>)
                    }
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-gray-700 mb-1">Target Amount (INR)</label>
                  <input type="number" value={targetForm.target_amount} onChange={e => setTargetForm({ ...targetForm, target_amount: e.target.value })} placeholder="e.g. 500000"
                    data-testid="target-amount-input"
                    className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" />
                </div>
              </div>
              <p className="text-[10px] text-gray-400">Year: {year} | Period type: {periodType}. If a target already exists for this salesman/period, it will be updated.</p>
            </div>
            <div className="flex justify-end gap-2 p-5 border-t border-gray-100">
              <button onClick={() => setShowSetTarget(false)} disabled={submitting} className="px-4 py-2 text-xs font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50">Cancel</button>
              <button onClick={handleSetTarget} disabled={submitting || !targetForm.salesman_id || !targetForm.target_amount} data-testid="save-target-btn"
                className="px-4 py-2 text-xs font-semibold text-white rounded-lg disabled:opacity-50 flex items-center gap-2 hover:shadow-lg hover:shadow-blue-200"
                style={{ backgroundColor: '#24549c' }}>
                {submitting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Target className="w-3 h-3" />}
                Save Target
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalesTargets;
